import React, { memo, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Box, Typography, Backdrop, Snackbar, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import * as pbi from 'powerbi-client';
import Main from '../Main/Main';
import HistoricoAcesso from '../commom/HistoricoAcesso';
import logoPowerBI from '../../assets/img/powerBI.png';
import FiltroPowerBI from './FiltroPowerBI';
import DashBordPowerBI from './DashBordPowerBI';
import LoaderMain from '../Onboarding/shared/LoaderMain';
import useFiltroPowerBIHook from './usePowerBIHook';
import { textoStatus, getStatusIcon } from './PowerBIUtils';

export const powerbiService = new pbi.service.Service(
  pbi.factories.hpmFactory,
  pbi.factories.wpmpFactory,
  pbi.factories.routerFactory
);

const PowerBI = memo(props => {
  const [embedConfig, setEmbedConfig] = useState({
    id: null,
    token: null,
    url: null,
    showFilterNav: false,
    exibeEdicao: false
  });
  const { id, token, url, showFilterNav, exibeEdicao } = embedConfig;
  const reportRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const doRefreshRef = useRef(() => {});
  const [selected, setSelected] = useState({ workspace: null, report: null });
  const [data, setData] = useState({ workspaces: [], reports: [] });
  const [alert, setAlert] = useState({ message: '', type: 'success' });
  const [loading, setLoading] = useState(false);

  const handleReportReady = React.useCallback(report => {
    reportRef.current = report;
  }, []);

  const handleTokenExpired = React.useCallback(() => {
    doRefreshRef.current();
  }, []);

  const resetEmbed = useCallback(() => {
    setEmbedConfig({ id: null, token: null, url: null, showFilterNav: false, exibeEdicao: false });
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    reportRef.current = null;
  }, []);

  const handleAlertClose = useCallback((e, reason) => {
    if (reason !== 'clickaway') setAlert({ message: '', type: 'success' });
  }, []);

  const handleViewReport = useCallback(payload => {
    setEmbedConfig({
      id: payload.codRelatorio,
      token: payload.token,
      url: payload.url,
      showFilterNav: payload.liberaNavegacaoFiltro,
      exibeEdicao: payload.exibeEdicao
    });
    if (payload.expiracao) {
      const schedule = expiracaoMs => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        const tempoExpiracao = 2 * 60 * 1000;
        let delay = expiracaoMs - Date.now() - tempoExpiracao;
        if (delay <= 0) delay = 5000;
        refreshTimerRef.current = setTimeout(() => doRefreshRef.current(), delay);
      };
      schedule(payload.expiracao);
    }
  }, []);

  const {
    loadingWorkspaces,
    handleWorkspaceChange,
    handleReportChange,
    handleViewReportClick,
    refreshEmbeddedToken
  } = useFiltroPowerBIHook({
    selected,
    setSelected,
    data,
    setData,
    setLoading,
    setAlert,
    onViewReport: handleViewReport,
    resetEmbed
  });

  const doRefresh = useCallback(async () => {
    if (!reportRef.current) {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      return;
    }
    try {
      const payload = await refreshEmbeddedToken();
      if (!payload || !payload.token) {
        setAlert({
          message: 'Houve uma falha ao encontrar o token de acesso. Favor contatar o suporte.',
          type: 'error'
        });
        resetEmbed();
      }

      await reportRef.current.setAccessToken(payload.token);
      setEmbedConfig(prev => ({
        ...prev,
        token: payload.token
      }));

      if (payload.expiracao) {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        const ahead = 2 * 60 * 1000;
        let delay = payload.expiracao - Date.now() - ahead;
        if (delay <= 0) delay = 5000;
        refreshTimerRef.current = setTimeout(() => doRefreshRef.current(), delay);
      }
    } catch (err) {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      resetEmbed();
    }
  }, [refreshEmbeddedToken, resetEmbed, setAlert]);

  useEffect(() => {
    doRefreshRef.current = doRefresh;
  }, [doRefresh]);

  const statusHeader = useMemo(() => {
    const dados = selected && selected.report && selected.report.dadosAtualizacao;
    if (!dados) return null;
    const { tipo, descricao, datePhrase } = textoStatus(dados);
    if (!descricao) return null;

    return (
      <>
        {getStatusIcon(tipo)}
        <span>Status:&nbsp;</span>
        <span title={datePhrase ? `${descricao} - ${datePhrase}` : descricao}>
          {descricao}
          {datePhrase ? ` - ${datePhrase}` : ''}
        </span>
      </>
    );
  }, [selected && selected.report && selected.report.dadosAtualizacao]);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  return (
    <>
      <Box
        position="relative"
        className={`relatorioBI backGroundBase ${token ? 'dashBoardPowerBI' : 'filtroPowerBI'}`}
      >
        <Main
          title="Inteligência de dados"
          Header={() => (
            <Box
              display="flex"
              alignItems="center"
              className={`pad-right-left-18 ${token ? 'margin-neg-top-40 pad-top-10' : ''}`}
            >
              {token ? (
                <>
                  <ArrowBackIcon onClick={resetEmbed} className="margin-right-10 cursor-pointer" />
                  <Typography className="texto-cor-cinza-escuro flex-1">
                    {selected.workspace && selected.workspace.nome}
                    <span className="font-bold-600"> / </span>
                    <span className="font-bold-600">{selected.report && selected.report.nome}</span>
                  </Typography>
                  {statusHeader && (
                    <Box className="font-size-15 margin-left-18" display="flex" alignItems="center">
                      {statusHeader}
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <img src={logoPowerBI} alt="Logo Power BI" className="w-30px margin-right-8" />
                  <Typography variant="h6" className="texto-cor-cinza-escuro font-bold-600 flex-1">
                    Inteligência de dados
                  </Typography>
                </>
              )}
            </Box>
          )}
          {...props}
        >
          {!token ? (
            <FiltroPowerBI
              data={data}
              selected={selected}
              loadingWorkspaces={loadingWorkspaces}
              onWorkspaceChange={handleWorkspaceChange}
              onReportChange={handleReportChange}
              onViewReportClick={handleViewReportClick}
            />
          ) : (
            <DashBordPowerBI
              id={id}
              token={token}
              url={url}
              showFilterNav={showFilterNav}
              exibeEdicao={exibeEdicao}
              powerbi={powerbiService}
              setAlert={setAlert}
              onReportReady={handleReportReady}
              onTokenExpired={handleTokenExpired}
            />
          )}
          <HistoricoAcesso codTela={1316} />
        </Main>

        <Backdrop
          open={loading}
          className="backDropLoading"
          sx={{ zIndex: theme => theme.zIndex.modal }}
        >
          <LoaderMain tipoLoader="apenas_loader" tipoImagem="circulo" tamanho="lg" />
        </Backdrop>
      </Box>

      <Snackbar
        open={Boolean(alert.message)}
        autoHideDuration={5000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        className="snackbar-cliente"
      >
        <Alert onClose={handleAlertClose} severity={alert.type} className="w-100">
          <span className="w-30px texto-semi-negrito font-1rem">{alert.message}</span>
        </Alert>
      </Snackbar>
    </>
  );
});

export default PowerBI;
